import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Snap to paise then always round up to a whole rupee.
 * 10.0000001 -> 10, 10.01 -> 11, 5432.40 -> 5433
 */
export function ceilRupee(amount: number): number {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return 0;
  const paise = Math.round(n * 100);
  return Math.ceil(paise / 100);
}

/**
 * Canonical payable: ceil once on the sum of base + markup + xmarkup.
 * Do not ceil each part separately.
 */
export function getPayableTotal(parts: {
  base: number;
  markup?: number;
  xmarkup?: number;
}): number {
  return ceilRupee(
    (Number(parts.base) || 0) +
      (Number(parts.markup) || 0) +
      (Number(parts.xmarkup) || 0),
  );
}

/** Payable / totals: ceil to whole rupee, then format. */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(ceilRupee(amount));
}

/**
 * Breakdown line items only — exact to paise, no ceil.
 * Sum of lines can be a few paise under Amount To Pay (the ceil remainder).
 */
export function formatRupeeExact(amount: number): string {
  const n = Number(amount);
  const value = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Wallet / ledger amounts. Do not use formatToRupee — ceilRupee maps
 * non-positive values to 0, which hides due balances.
 */
export function formatWalletRupee(
  amount: number,
  opts?: { absolute?: boolean },
): string {
  const n = Number(amount);
  const value = Number.isFinite(n) ? n : 0;
  return formatRupeeExact(opts?.absolute ? Math.abs(value) : value);
}

export function getAvailableCredit(balance: number, limit: number): number {
  const available = Number(balance) + Number(limit);
  return Number.isFinite(available) && available > 0 ? available : 0;
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date);
}
